import { Skeleton } from "@mui/lab";
import { Card, CardContent, createStyles, Grid, Theme } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Knowledge";
import { FeedStyleClasses, GlobalClassKeys, TimelineStyleClasses } from "assets/jss/common-styles";
import { AppMessageBox } from "components/AppMessageBox";
import { LoadingIndicator } from "components/CallServer/LoadingIndicator";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import MuiContainer from "components/MuiExtras/MuiContainer";
import WidgetDrawer from "components/WidgetDrawer";
import { deepMerge } from "lib/DeepMerge";
import { FeedTopicDto } from "models/ApiModels";
import * as React from "react";
import ReactResizeDetector from "react-resize-detector";
import { RouteComponentProps } from "react-router";
import { SidebarPostWindowMessages } from "store/sidebar/windowMessages";
import FeedTopic from "views/Timeline/Components/FeedTopic";
import TopicRelationsWidget from "views/Topic/Widgets/TopicRelationsWidget";
import TopicResumeWidget from "views/Topic/Widgets/TopicResumeWidget";
import TopicWhoKnowsAboutWidget from "views/Topic/Widgets/TopicWhoKnowsAboutWidget";
import TopicCardHeader from "./TopicCardHeader";

/////////////////////////////////////////////////////////////////////////////////////

const styles = (theme: Theme) => createStyles({
    ...TimelineStyleClasses(theme),
    ...FeedStyleClasses(theme)
});

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    lastError: any;
    loading: boolean;
    dialog: any;
    data: FeedTopicDto;
    relationShipTimestamp: Date;
}

interface IProps {
    topicId: string;
    timestamp: Date;
}

type Props = IProps & WithStyles<typeof styles> & RouteComponentProps;

/////////////////////////////////////////////////////////////////////////////////////

class TopicBaseTimeline extends React.PureComponent<Props, IState> {

    private sidebarWindowMessages: SidebarPostWindowMessages;

    ///
    constructor(props: Props) {
        super(props);
        this.state = {
            data: null,
            dialog: null,
            loading: true,
            lastError: null,
            relationShipTimestamp: new Date()
        };

        this.sidebarWindowMessages = new SidebarPostWindowMessages(window.opener || window);
    }

    ///
    private onLoad(topic: string) {
        this.setState({ loading: true, lastError: null, data: null });

        api.getTopicFeedHeader(topic)
            .then((data) => {
                this.setState({ data, loading: false });
            })
            .catch((error) => {
                this.setState({ loading: false, lastError: error });
            });
    }

    ///
    private renderSkeleton() {
        return (
            <Grid container spacing={1} direction="row" wrap="nowrap" className={GlobalClassKeys.grow}>
                <Grid item className={GlobalClassKeys.grow}>
                    <div className={this.props.classes.timelineContainer}>
                        <Card>
                            <CardContent>

                                <MuiContainer margin="dense">
                                    <Grid container spacing={2} >
                                        <Grid item>
                                            <Skeleton animation="wave" variant="rectangular" width={118} height={118} />
                                        </Grid>

                                        <Grid item xs>
                                            <MuiContainer margin="dense">
                                                <Grid container justifyContent="space-between">
                                                    <Grid item xs>
                                                        <Skeleton animation="wave" height={24} width="100%" style={{ marginBottom: 6 }} />

                                                        <MuiContainer margin="dense">
                                                            <Skeleton animation="wave" height={22} width="50%" style={{ marginBottom: 6 }} />
                                                        </MuiContainer>

                                                    </Grid>
                                                </Grid>
                                            </MuiContainer>
                                        </Grid>
                                    </Grid>
                                </MuiContainer>

                                <Grid container
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Grid item>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item>
                                                <Skeleton variant="rectangular" width={50} height={30} />
                                            </Grid>
                                            <Grid item>
                                                <Skeleton variant="rectangular" width={16} height={16} />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item className={this.props.classes.cardActions}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item>
                                                <Skeleton variant="rectangular" width={16} height={16} />
                                            </Grid>
                                            <Grid item>
                                                <Skeleton variant="rectangular" width={16} height={16} />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                        <LoadingIndicator />
                    </div>
                </Grid>
                <Grid item style={{ width: 350 }}>
                    <Skeleton animation="wave" height={24} width="100%" style={{ marginBottom: 6 }} />
                    <Skeleton animation="wave" height={80} width="100%" />
                </Grid>
            </Grid>
        );
    }

    ///
    private handleInfo = () => {
        const { data } = this.state;
        this.setState({
            dialog: (
                <WidgetDrawer onClose={() => this.setState({ dialog: null })} >
                    <TopicWhoKnowsAboutWidget topicId={data.topicId} />
                    <TopicResumeWidget data={data.statistics} />
                </WidgetDrawer>
            )
        });
    };

    ///
    private handleRelationshipChanged = () => {
        this.setState({ relationShipTimestamp: new Date() });
    };

    ///
    public componentDidMount() {
        this.onLoad(this.props.topicId);
    }

    ///
    public componentDidUpdate(prevProps: Props) {
        if (prevProps.topicId != this.props.topicId || (prevProps.timestamp != this.props.timestamp && !this.state.loading))
            this.onLoad(this.props.topicId);
    }

    ///
    public render() {
        const { classes } = this.props;
        const { data, lastError, loading, relationShipTimestamp } = this.state;

        if (lastError != null) {
            return <RequestErrorReport error={lastError} />;
        }

        if (loading) {
            return this.renderSkeleton();
        }

        if (data == null) {
            return <AppMessageBox resource="TopicNotFound" variant="warning" />;
        }
        else if (!data.permissions.hasAccess) {
            // Se está na mesma janela, reinicia a lista de tópicos.
            if (window.opener == null) this.sidebarWindowMessages.reset();

            return <AppMessageBox resource="TopicPermissionDenied" />;
        }

        return (
            <>
                <ReactResizeDetector handleWidth refreshMode="debounce" refreshRate={0}>
                    {
                        (d: ReactResizeDetectorDimensions) =>

                            <Grid container spacing={1} direction="row" wrap="nowrap" className={GlobalClassKeys.grow}>
                                <Grid item className={GlobalClassKeys.grow}>

                                    <div className={classes.timelineContainer}>

                                        <TopicCardHeader
                                            key={data.topicId}
                                            topic={data}
                                            classes={classes}
                                            showRelationsButton={d.width < 920}
                                            onInfoRequest={d.width < 920 ? this.handleInfo : null}
                                            onRelationshipChanged={this.handleRelationshipChanged}
                                            onUpdateCard={
                                                (newData) => {

                                                    if (this.state.data.name != newData.name || this.state.data.interaction.following != newData.interaction.following)
                                                        this.sidebarWindowMessages.reset();

                                                    this.setState({
                                                        data: deepMerge(this.state.data, newData, { arrayMerge: (target: any, source: any, options: any) => source })
                                                    });
                                                }
                                            }
                                        />

                                        <FeedTopic
                                            topicId={this.props.topicId}
                                            classes={classes}
                                            fullView={false}
                                        />
                                    </div>

                                </Grid>

                                {
                                    d.width > 919 &&
                                    <Grid item style={{ width: 350 }}>
                                        <TopicWhoKnowsAboutWidget topicId={data.topicId} />
                                        <TopicRelationsWidget topicId={data.topicId} timestamp={relationShipTimestamp} />
                                        <TopicResumeWidget data={data.statistics} />
                                    </Grid>
                                }
                            </Grid>


                    }
                </ReactResizeDetector>

                {this.state.dialog}
            </>
        );
    }
}

export default withStyles(styles)(TopicBaseTimeline);